/**
 * Reglas del plazo de pago, compartidas por los documentos que lo piden:
 * Facturación, Facturación recurrente, Cotización, Órdenes de compra y Pedidos.
 *
 * Estaban duplicadas en cada formulario y habían divergido: Facturación
 * filtraba con `plazosParaContacto` y los otros cuatro con un
 * `termino.dias <= contacto.plazo.dias` escrito en el JSX, que dejaba pasar el
 * vencimiento manual (`dias: null`, y en JS `null <= n` es true) y dejaba el
 * select completamente vacío cuando el cliente no tenía plazo asignado.
 */

/**
 * Valor del select cuando todavía no se eligió plazo.
 *
 * `value` es cadena vacía a propósito. react-select publica ese valor en un
 * input oculto llamado `plazo_id` y el submit lo manda tal cual con
 * `new FormData(form)`. Con el `value: 0` que se usaba antes, el API recibía
 * `plazo_id=0`, la validación lo dejaba pasar —`required` no rechaza el cero— y
 * el insert moría contra la FK `facturas.plazo_id -> terminos_pago.id` con un
 * 500. En vacío el API responde 422 y el formulario pinta el error en el campo.
 */
export const PLAZO_VACIO = { value: "", label: "Seleccione...", dias: 0 };

/**
 * Contra entrega se identifica por el nombre, igual que en el API: las vistas de
 * `v_documento_cobros` lo separan del crédito con `nombre LIKE '%contra%'`. No
 * hay bandera ni `dias` reservado para él, y cada sucursal crea el suyo a mano
 * —no sale de CreateSucursalDefaultData—, así que el nombre es lo único común.
 */
const esContraEntrega = (termino = {}) => /contra/i.test(termino.label || "");

/**
 * Plazos que el cliente puede usar: los que no exceden el que tiene asignado.
 *
 * Facturación tuvo encima un recorte por `credito_disponible <= 0`. En JS
 * `null <= 0` es true, así que todo cliente con el campo en null o en 0 —la
 * mayoría— quedaba reducido a Contado. No se reproduce: para volver a limitar
 * por crédito hace falta un campo que distinga "sin crédito" de "sin calcular".
 */
export const plazosParaContacto = (plazos = [], contacto = {}) => {
	// Cliente sin plazo asignado: puede comprar de contado o contra entrega, que
	// son los dos términos que no le abren crédito. Sin este caso el select se
	// quedaba completamente vacío y no se podía guardar el documento.
	if (!contacto.plazo) {
		return plazos.filter(
			(termino) => termino.dias === 0 || esContraEntrega(termino)
		);
	}

	const limite = contacto.plazo.dias;

	return plazos.filter((termino) => {
		if (termino.dias === 0) return true;

		// dias === null es el plazo de fecha manual y no admite comparación; el
		// término asignado al cliente se acepta siempre por identidad. Se compara
		// como string porque el API mezcla ids numéricos y de texto.
		if (String(termino.value) === String(contacto.plazo.id)) return true;

		return limite !== null && termino.dias !== null && termino.dias <= limite;
	});
};
